// MARK://dhk8/pos/stores/order-store
// Zustand store — UI cache of the active order. Authoritative state lives in
// dhk8-pos-orders; this store holds an adapted (UI-friendly) copy and routes
// mutations through lib/clients/orders.ts. On a network failure a mutation is parked
// in the offline outbox (lib/offline-queue.ts) and replayed by flushOutbox() on
// reconnect; an optimistic synthetic line is shown meanwhile.
//
// adaptOrder() is the anti-corruption layer: the backend OrderDto is rich (tenantId,
// per-item subAccountId, kitchen timestamps, discountMeta, …); we project it to the
// shape the UI components consume (items grouped into sub-accounts, status collapsed
// to OPEN|SENT|READY|CLOSED|CANCELLED).

import { create } from 'zustand'

import { ApiError, apiFetch, getTenantId } from '../lib/api.js'
import { apiOrigin } from '../lib/api-origin.js'
import {
  enqueue, flush, onOutboxChange, newIdempotencyKey,
  type OutboxEntry,
} from '../lib/offline-queue.js'
import { useAuthStore } from './auth-store.js'
import * as orders from '../lib/clients/orders.js'
import { getPrintingConfig, pushPrintHistory } from '../lib/bridge/printing-config-store.js'
import { printReceipt } from '../lib/bridge/bridge-client.js'
import { trackRelayJob } from '../lib/bridge/relay-job-tracker.js'
import { toKitchen } from '../lib/bridge/canonical-receipt.js'
import { getReceiptOpts } from '../lib/print/receipt-opts.js'

// ── DTOs (UI shape — the rest of the app imports these from here) ────────────

export interface OrderItemModifier {
  modifierId: string
  name: string
  priceDeltaCents: number
}

export interface OrderItem {
  id: string
  productId: string
  name: string
  quantity: number
  unitPriceCents: number
  totalCents: number
  taxRate: string | number
  modifiers: OrderItemModifier[]
  notes?: string
  status: 'PENDING' | 'SENT' | 'PREPARING' | 'READY' | 'DELIVERED' | 'CANCELLED'
}

export interface SubAccount {
  id: string
  label: string
  items: OrderItem[]
  subtotalCents: number
  taxCents: number
  totalCents: number
  paidCents: number
  /** True for the synthetic "Principal" account that always holds items not yet
   *  assigned to a real sub-account. Never sent to the backend. */
  isPrincipal?: boolean
}

/** Sentinel id of the synthetic "Principal" sub-account (the unassigned-items bucket
 *  that's always present once an order has ≥1 real sub-account). */
export const PRINCIPAL_SUB_ID = '__principal__'

export interface OrderDto {
  id: string
  folio: string
  type: 'DINE_IN' | 'TAKEOUT' | 'DELIVERY' | 'DRIVE_THROUGH'
  status: 'OPEN' | 'SENT' | 'READY' | 'CLOSED' | 'CANCELLED'
  /** Código de recolección (para-llevar/domicilio/drive) = el ref que se canta y
   *  que muestra el cliente. orderRef = pickupCode ?? folio. La comanda de cocina
   *  lo imprime GRANDE para casar con /para-llevar, /orders/pending y el cliente. */
  pickupCode?: string | null
  tableSessionId?: string
  covers: number
  items: OrderItem[]
  subAccounts: SubAccount[]
  subtotalCents: number
  taxCents: number
  discountCents: number
  totalCents: number
  tipCents: number
  paidCents?: number
  openedAt?: string
  closedAt?: string | null
  /** UUIDv7 público para descargar el ticket en tickets.nintaigo.com/{uuid} (QR). Null
   *  hasta que la orden cierra (backend recordPayment lo asigna); para órdenes legacy
   *  cerradas antes del 2026-05-23 21:32 UTC, el backend hace backfill on-read. */
  publicTicketUuid?: string | null
  /** Id del cajero que cerró la orden — alimenta el "Atendió" del preview. Backend
   *  pos-orders lo expone como `closedByUserId`. Null para órdenes OPEN. */
  closedByUserId?: string | null
  /** Método de pago que el comensal preseleccionó al ordenar (CASH/TERMINAL/ONLINE).
   *  Lo usa la Barra para preseleccionar el método al cobrar (editable). Null en
   *  órdenes de staff o sin elección. */
  chosenPaymentMethod?: string | null
}

export type PaymentMethod =
  | 'CASH'
  | 'CARD_DEBIT'
  | 'CARD_CREDIT'
  | 'TRANSFER'
  | 'MEAL_VOUCHER'
  | 'OTHER'

/**
 * Lane #187 (2026-05-26) — UI maneja métodos granulares (CARD_DEBIT vs
 * CARD_CREDIT, MEAL_VOUCHER) pero el schema del backend pos-orders sólo
 * acepta los 5 SAT-friendly (CASH, CARD, TRANSFER, VOUCHER, OTHER). Esta
 * función normaliza para evitar 400 VALIDATION_ERROR cuando el cajero
 * cobra con tarjeta en la barra:
 *   `Invalid enum value. Expected 'CASH'|'CARD'|... received 'CARD_DEBIT'`
 * El detalle débito/crédito se conserva localmente en el ticket POS;
 * el backend recibe la categoría agregada.
 */
function toBackendPaymentMethod(m: PaymentMethod | string): 'CASH' | 'CARD' | 'TRANSFER' | 'VOUCHER' | 'OTHER' {
  if (m === 'CARD_DEBIT' || m === 'CARD_CREDIT') return 'CARD'
  if (m === 'MEAL_VOUCHER') return 'VOUCHER'
  if (m === 'CASH' || m === 'TRANSFER' || m === 'OTHER') return m
  if (m === 'CARD' || m === 'VOUCHER') return m
  return 'OTHER'
}

export interface RegisteredPayment {
  id: string
  method: PaymentMethod | string
  amountCents: number
  tipCents: number
  cardLastFour: string | null
  referenceCode: string | null
  createdAt: string
  subAccountId?: string | null
}

export interface PaymentDraft {
  method: PaymentMethod
  amountCents: number
  tipCents?: number
  /** Cash handed by the customer (cash tenders) — change = max(0, cashGivenCents - amountCents). */
  cashGivenCents?: number
  changeCents?: number
  cardLastFour?: string
  subAccountId?: string
  idempotencyKey?: string
}

export type DiscountKind = 'PERCENT' | 'AMOUNT'
export type DiscountTarget = 'ORDER' | 'SUB_ACCOUNT' | 'ITEM'

export interface DiscountDraft {
  /** Only ORDER is wired to dhk8-pos-orders today (POST /discount is order-level).
   *  TODO(reconcile): SUB_ACCOUNT / ITEM discounts when the backend supports them. */
  target: DiscountTarget
  targetId?: string
  kind: DiscountKind
  value: number
  reason?: string
  authPin?: string
}

export interface DraftModifier {
  modifierId: string
  name: string
  priceDeltaCents: number
}

export interface DraftItem {
  productId: string
  variantId?: string
  variantName?: string
  name: string
  unitPriceCents: number
  taxRate: string
  quantity: number
  modifiers: DraftModifier[]
  notes?: string
}

export interface TableContext {
  tableSessionId: string | null
  tableCode: string | null
  covers: number
  waiterName: string | null
}

// ── session storage helpers ──────────────────────────────────────────────────

const CURRENT_ORDER_KEY = 'dhk8_pos_current_order_id'

function readCurrentOrderId(): string | null {
  if (typeof window === 'undefined') return null
  try { return window.sessionStorage.getItem(CURRENT_ORDER_KEY) } catch { return null }
}
function writeCurrentOrderId(id: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (id) window.sessionStorage.setItem(CURRENT_ORDER_KEY, id)
    else window.sessionStorage.removeItem(CURRENT_ORDER_KEY)
  } catch { /* noop */ }
}
export function getPersistedOrderId(): string | null {
  return readCurrentOrderId()
}

// ── adapter: backend OrderDto → UI OrderDto ──────────────────────────────────

const BACKEND_STATUS: Record<orders.BackendOrderDto['status'], OrderDto['status']> = {
  // Órdenes guest pendientes de aceptación: existen y son editables por staff,
  // aún no comandadas → se muestran como OPEN. REJECTED es terminal → CANCELLED.
  PENDING_ACCEPTANCE: 'OPEN',
  OPEN: 'OPEN',
  SENT_TO_KITCHEN: 'SENT',
  PARTIALLY_PAID: 'SENT',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
  REJECTED: 'CANCELLED',
}

function adaptItem(i: orders.BackendOrderItemDto): OrderItem {
  return {
    id: i.id,
    productId: i.productId,
    name: i.name,
    quantity: i.quantity,
    unitPriceCents: i.unitPriceCents,
    totalCents: i.totalCents,
    taxRate: i.taxRate,
    modifiers: i.modifiers.map((m) => ({ modifierId: m.modifierId, name: m.name, priceDeltaCents: m.priceDeltaCents })),
    ...(i.notes ? { notes: i.notes } : {}),
    status: i.status,
  }
}

function adaptOrder(raw: orders.BackendOrderDto): OrderDto {
  const liveItems = raw.items.filter((i) => i.status !== 'CANCELLED')
  const items = liveItems.map(adaptItem)
  const bySubAccount = new Map<string, OrderItem[]>()
  const principalItems: OrderItem[] = []
  for (const ri of liveItems) {
    if (!ri.subAccountId) { principalItems.push(adaptItem(ri)); continue }
    const arr = bySubAccount.get(ri.subAccountId) ?? []
    arr.push(adaptItem(ri))
    bySubAccount.set(ri.subAccountId, arr)
  }
  const realSubs: SubAccount[] = raw.subAccounts.map((sa) => {
    const saItems = bySubAccount.get(sa.id) ?? []
    const subtotalCents = saItems.reduce((s, it) => s + it.totalCents, 0)
    return {
      id: sa.id,
      label: sa.label,
      items: saItems,
      subtotalCents,
      taxCents: 0,
      totalCents: Math.max(0, subtotalCents - sa.discountCents) + sa.tipCents,
      paidCents: 0,
    }
  })
  // When the order has any real sub-account, expose a synthetic "Principal" bucket
  // first — it holds every item not yet assigned to a sub-account, so nothing ever
  // "disappears" after a split and items can be moved back to it. Order-level
  // discount lands on Principal (sub-scoped discounts already live on their sub).
  let subAccounts: SubAccount[] = realSubs
  if (raw.subAccounts.length > 0) {
    const principalSubtotal = principalItems.reduce((s, it) => s + it.totalCents, 0)
    const principal: SubAccount = {
      id: PRINCIPAL_SUB_ID,
      label: 'Principal',
      items: principalItems,
      subtotalCents: principalSubtotal,
      taxCents: 0,
      totalCents: Math.max(0, principalSubtotal - raw.discountCents),
      paidCents: 0,
      isPrincipal: true,
    }
    subAccounts = [principal, ...realSubs]
  }
  return {
    id: raw.id,
    folio: raw.folio,
    type: raw.type,
    status: BACKEND_STATUS[raw.status] ?? 'OPEN',
    pickupCode: raw.pickupCode ?? null,
    ...(raw.tableSessionId ? { tableSessionId: raw.tableSessionId } : {}),
    covers: raw.covers,
    items,
    subAccounts,
    subtotalCents: raw.subtotalCents,
    taxCents: raw.taxCents,
    discountCents: raw.discountCents,
    totalCents: raw.totalCents,
    tipCents: raw.tipCents,
    paidCents: raw.paidCents,
    openedAt: raw.openedAt,
    closedAt: raw.closedAt,
    publicTicketUuid: raw.publicTicketUuid ?? null,
    closedByUserId: raw.closedByUserId ?? null,
    chosenPaymentMethod: raw.chosenPaymentMethod ?? null,
  }
}

function computePendingIds(order: OrderDto): Set<string> {
  return new Set(order.items.filter((i) => i.status === 'PENDING').map((i) => i.id))
}

/**
 * True ONLY for a genuine transport failure (the device is offline / fetch never
 * reached the server), in which case the mutation is parked in the outbox. A server
 * that DID respond with an HTTP error (4xx/5xx) or a GraphQL error must be rethrown
 * so the cashier sees it - never silently queued.
 *
 * The order write path is urql/GraphQL now (F8.1). urql wraps every failure in a
 * `CombinedError` whose `message` ALWAYS starts with "[Network] ..." - even a 409
 * (e.g. "[Network] No Content"). So we MUST inspect the error STRUCTURE, not the text:
 *   - `graphQLErrors` non-empty            -> server executed, returned errors -> RETHROW
 *   - `response` present (the Fetch Response) -> server replied with an HTTP status -> RETHROW
 *   - `networkError` present, NO `response` -> fetch itself failed (offline)     -> OFFLINE
 * urql sets `response` whenever `fetch` RESOLVED (any non-2xx HTTP), and leaves it
 * undefined only when `fetch` REJECTED (TypeError 'Failed to fetch' / NetworkError) -
 * that's the real offline signal. `navigator.onLine === false` is an extra offline cue.
 * Legacy REST callers (payments) still throw `ApiError` -> that's an HTTP error -> RETHROW.
 */
interface UrqlLikeError {
  graphQLErrors?: unknown[]
  networkError?: unknown
  response?: unknown
}
function asUrqlError(e: unknown): UrqlLikeError | null {
  if (e === null || typeof e !== 'object') return null
  const c = e as UrqlLikeError
  if ('graphQLErrors' in c || 'networkError' in c || 'response' in c) return c
  return null
}
function isTransportOffline(e: unknown): boolean {
  // A REST ApiError is an HTTP error response by construction - never offline.
  if (e instanceof ApiError) return false
  const c = asUrqlError(e)
  if (c) {
    // The server executed the operation and returned GraphQL errors -> not offline.
    if (Array.isArray(c.graphQLErrors) && c.graphQLErrors.length > 0) return false
    // The server replied with an HTTP status (urql attaches the Fetch Response) ->
    // a real 4xx/5xx (incl. the 409 "[Network] No Content" case) -> not offline.
    if (c.response != null) return false
    // networkError WITHOUT a response = fetch never completed = genuine transport failure.
    if (c.networkError != null) return true
  }
  // Plain fetch rejection that didn't go through urql (e.g. a direct apiFetch call that
  // failed before getting a Response): a TypeError or a 'Failed to fetch'-style message.
  if (e instanceof TypeError) return true
  if (e instanceof Error && /failed to fetch|networkerror when attempting to fetch|network request failed/i.test(e.message)) {
    return true
  }
  // Last resort: the browser says it's offline.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  return false
}

const OFFLINE_PREFIX = 'offline:'

function makeOfflineItem(draft: DraftItem): OrderItem {
  // `draft.unitPriceCents` is the BASE unit price (product + variant); the
  // modifier deltas live in `draft.modifiers`. Mirror the dhk8-pos-orders
  // formula — lineGross = (unitPriceCents + Σ modifier deltas) * quantity — so
  // an offline-added line shows the same total the backend will compute on sync.
  const modDelta = draft.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0)
  return {
    id: `${OFFLINE_PREFIX}${newIdempotencyKey()}`,
    productId: draft.productId,
    name: draft.name,
    quantity: draft.quantity,
    unitPriceCents: draft.unitPriceCents,
    totalCents: (draft.unitPriceCents + modDelta) * draft.quantity,
    taxRate: draft.taxRate,
    modifiers: draft.modifiers.map((m) => ({ modifierId: m.modifierId, name: m.name, priceDeltaCents: m.priceDeltaCents })),
    notes: draft.notes ? `${draft.notes} · ⏳ offline` : '⏳ offline',
    status: 'PENDING',
  }
}

function bumpTotals(order: OrderDto, deltaCents: number): OrderDto {
  return { ...order, subtotalCents: order.subtotalCents + deltaCents, totalCents: order.totalCents + deltaCents }
}

function authCtx(): { tenantId: string; branchId: string; userId: string } {
  const user = useAuthStore.getState().user
  const tenantId = getTenantId()
  if (!tenantId || !user) throw new Error('Sesión no inicializada (tenant / usuario)')
  return { tenantId, branchId: user.branchId, userId: user.id }
}

function draftToAddItemInput(d: DraftItem): orders.AddItemInput {
  return {
    productId: d.productId,
    name: d.name,
    quantity: d.quantity,
    unitPriceCents: d.unitPriceCents,
    taxRate: d.taxRate,
    ...(d.variantId ? { variantId: d.variantId } : {}),
    ...(d.variantName ? { variantName: d.variantName } : {}),
    modifiers: d.modifiers.map((m) => ({ modifierId: m.modifierId, name: m.name, priceDeltaCents: m.priceDeltaCents })),
    ...(d.notes ? { notes: d.notes } : {}),
  }
}

let invalidateTimer: ReturnType<typeof setTimeout> | null = null

// ── store ────────────────────────────────────────────────────────────────────

interface OrderState {
  table: TableContext
  setTable: (ctx: Partial<TableContext>) => void
  clearTable: () => void
  order: OrderDto | null
  pendingItemIds: Set<string>
  payments: RegisteredPayment[]
  syncing: boolean
  outboxDepth: number
  createOrder: (input: { tableSessionId?: string; covers: number }) => Promise<OrderDto>
  /** Fast-bar / to-go sale: creates a TAKEOUT order, adds the lines, fires, takes a
   *  single payment and closes it — all in one call. Replaces the orchestration the
   *  sc-restaurant `POST /v1/orders/fast` endpoint did server-side.
   *  TODO backend: a real `POST /v1/orders/fast` on dhk8-pos-orders would make this
   *  atomic; today it's N round-trips. Does NOT touch the active-order state. */
  createFastSale: (input: {
    lines: {
      productId: string
      name: string
      quantity: number
      /** BASE unit price WITHOUT modifier deltas. The backend folds the deltas
       *  in via `lineGross()` — sending the foldeado price + modifiers would
       *  double-charge. If the caller doesn't have separate base/delta
       *  bookkeeping it can pass `unitPriceCents` and `modifiers: []`. */
      unitPriceCents: number
      taxRate?: string | number
      modifiers?: { name: string; priceDeltaCents: number }[]
      notes?: string
    }[]
    payment: { method: PaymentMethod; amountCents: number; cashGivenCents?: number; cardLastFour?: string }
    tipCents?: number
  }) => Promise<{ order: OrderDto; payments: RegisteredPayment[] }>
  /** Barra pago-mixto (hotfix): crea la orden TAKEOUT + ítems SIN fire (el 1er
   *  pago la dispara vía la lógica takeout del backend) e HIDRATA el store, para
   *  que `PaymentDialog` cobre en múltiples tenders hasta cerrar. */
  openFastSaleOrder: (input: {
    lines: {
      productId: string; name: string; quantity: number; unitPriceCents: number
      taxRate?: string | number; modifiers?: { name: string; priceDeltaCents: number }[]; notes?: string
    }[]
    tipCents?: number
  }) => Promise<OrderDto>
  addItem: (draft: DraftItem) => Promise<void>
  /** Cambia la cantidad de un item existente (PATCH /items/:id). Se usa para
   *  incrementar líneas PENDING en vez de duplicarlas — bug #171. */
  updateItemQty: (itemId: string, quantity: number) => Promise<void>
  removeItem: (itemId: string, managerAuthToken?: string) => Promise<void>
  sendToKitchen: () => Promise<void>
  cancelOrder: (reason: string, managerAuthToken?: string) => Promise<OrderDto>
  resetOrder: () => void
  hydrateOrder: (order: OrderDto) => void
  hydrateFromBackend: (orderId: string) => Promise<OrderDto | null>
  invalidateAndRefetch: () => void
  registerPayment: (draft: PaymentDraft) => Promise<RegisteredPayment>
  /** Persist the tip on the order (its total includes the tip). */
  setTip: (tipCents: number) => Promise<OrderDto>
  /** Alias of setTip — kept for the legacy "cobrar y cerrar" call site. */
  closeOrder: (tipCents: number) => Promise<OrderDto>
  createSubAccount: (label: string) => Promise<OrderDto>
  applyDiscount: (draft: DiscountDraft) => Promise<OrderDto>
  splitOrder: (draft: {
    mode: 'EVEN' | 'PERCENT' | 'ITEMS'
    parts?: number
    labels?: string[]
    percents?: number[]
    /** Map of orderItemId → sub-account index in `labels` (ITEMS mode only). */
    itemAssignments?: Record<string, number>
  }) => Promise<OrderDto>
  /** Move an existing order item into a sub-account (or back to "Principal" when
   *  `subAccountId` is null / PRINCIPAL_SUB_ID). Used by the cart's per-line picker
   *  and the ITEMS-split flow. */
  assignItemToSub: (itemId: string, subAccountId: string | null) => Promise<OrderDto>
  flushOutbox: () => Promise<{ sent: number; remaining: number }>
}

/** Crea una orden TAKEOUT y le agrega los ítems (SIN fire, SIN pago). Compartido
 *  por `createFastSale` (que además dispara + cobra) y `openFastSaleOrder` (que
 *  hidrata el store para el cobro mixto vía PaymentDialog). Pura orquestación,
 *  cero lógica de dinero. */
async function buildFastSaleTakeout(
  lines: {
    productId: string; name: string; quantity: number; unitPriceCents: number
    taxRate?: string | number; modifiers?: { name: string; priceDeltaCents: number }[]; notes?: string
  }[],
): Promise<orders.BackendOrderDto> {
  const { tenantId, branchId, userId } = authCtx()
  let raw = await orders.createOrder({ tenantId, branchId, type: 'TAKEOUT', covers: 1, openedByUserId: userId })
  // Cada modifier necesita un modifierId — snapshot sintético (los UUIDs reales
  // del catálogo aún no se propagan a este lado). Igual que en createFastSale.
  const mkModId = (): string => (globalThis.crypto?.randomUUID?.() ?? newIdempotencyKey())
  for (const l of lines) {
    const mods = (l.modifiers ?? []).map((m) => ({ modifierId: mkModId(), name: m.name, priceDeltaCents: m.priceDeltaCents }))
    raw = await orders.addOrderItem(raw.id, {
      productId: l.productId, name: l.name, quantity: l.quantity, unitPriceCents: l.unitPriceCents,
      ...(l.taxRate !== undefined ? { taxRate: l.taxRate } : {}),
      ...(l.notes ? { notes: l.notes } : {}),
      modifiers: mods,
    })
  }
  return raw
}

export const useOrderStore = create<OrderState>((set, get) => {
  onOutboxChange((depth) => set({ outboxDepth: depth }))

  return {
    table: { tableSessionId: null, tableCode: null, covers: 1, waiterName: null },
    setTable: (ctx) => set((s) => ({ table: { ...s.table, ...ctx } })),
    clearTable: () => set({ table: { tableSessionId: null, tableCode: null, covers: 1, waiterName: null } }),
    order: null,
    pendingItemIds: new Set(),
    payments: [],
    syncing: false,
    outboxDepth: 0,

    async createOrder(input) {
      const { tenantId, branchId, userId } = authCtx()
      const raw = await orders.createOrder({
        tenantId, branchId, type: 'DINE_IN', covers: input.covers, openedByUserId: userId,
        ...(input.tableSessionId ? { tableSessionId: input.tableSessionId } : {}),
      })
      const order = adaptOrder(raw)
      writeCurrentOrderId(order.id)
      set({ order, pendingItemIds: computePendingIds(order), payments: [] })
      return order
    },

    async createFastSale(input) {
      const { tenantId, branchId, userId } = authCtx()
      // 1-2) crear TAKEOUT + agregar ítems (helper compartido con openFastSaleOrder).
      let raw = await buildFastSaleTakeout(input.lines)
      // 3) fire to kitchen — dispara los items al KDS via evento
      // dhk8.pos.order.fired_to_kitchen. NO silenciar errores: si falla, la
      // orden queda con items en PENDING y el barista no se entera. Antes
      // (2026-05-25) el catch silencioso ocultaba el bug "items no llegan
      // a KDS". Ahora propagamos para que el caller pueda mostrar toast.
      raw = await orders.fireOrder(raw.id)
      // 4) tip (if any) then payment → order closes when fully paid
      const tipCents = input.tipCents ?? 0
      if (tipCents > 0) raw = await orders.setOrderTip(raw.id, tipCents)
      const idem = newIdempotencyKey()
      const backendMethod = toBackendPaymentMethod(input.payment.method)
      // F9.11b: el grafo (recordOrderPayment) persiste el PosPayment completo por-tender
      // (method + cardLastFour + reference + changeCents), idempotente. Se eliminó la
      // llamada REST a dhk8-pos-payments-mixed (stub 404, swallowed) — D2 cerrada.
      raw = await orders.recordOrderPayment(raw.id, {
        amountCents: input.payment.amountCents, method: backendMethod, tipCents,
        ...(input.payment.cashGivenCents !== undefined ? { changeCents: Math.max(0, input.payment.cashGivenCents - input.payment.amountCents) } : {}),
        ...(input.payment.cardLastFour ? { cardLastFour: input.payment.cardLastFour } : {}),
        capturedById: userId,
        // F9.6: same idem key as the payment write — a double-tap dedupes to one payment.
        idempotencyKey: idem,
      })
      // La propina la registra pos-tips al consumir `dhk8.pos.order.closed`
      // (server-side, idempotente por orden). Ver el comentario del otro sitio.
      const order = adaptOrder(raw)
      const reg: RegisteredPayment = {
        id: idem,
        method: input.payment.method,
        amountCents: input.payment.amountCents,
        tipCents,
        cardLastFour: input.payment.cardLastFour ?? null,
        referenceCode: idem,
        createdAt: new Date().toISOString(),
      }
      return { order, payments: [reg] }
    },

    async openFastSaleOrder(input) {
      // Pasos 1-2 compartidos (crear TAKEOUT + ítems), SIN fire: el PRIMER pago
      // que registre PaymentDialog dispara a cocina vía la lógica takeout del
      // backend (isTakeoutAwaitingFire). Hidrata el store para el cobro mixto.
      let raw = await buildFastSaleTakeout(input.lines)
      const tipCents = input.tipCents ?? 0
      if (tipCents > 0) raw = await orders.setOrderTip(raw.id, tipCents)
      const order = adaptOrder(raw)
      writeCurrentOrderId(order.id)
      set({ order, pendingItemIds: computePendingIds(order), payments: [] })
      return order
    },

    async addItem(draft) {
      const current = get().order
      if (!current) throw new Error('No hay orden abierta')
      try {
        const next = adaptOrder(await orders.addOrderItem(current.id, draftToAddItemInput(draft)))
        set({ order: next, pendingItemIds: computePendingIds(next) })
      } catch (e) {
        if (!isTransportOffline(e)) throw e
        // Park the SAME graph mutation the online path uses (addOrderItem), not a dead
        // REST descriptor. flushOutbox() replays it via gqlClient on reconnect. The
        // idempotencyKey rides on the entry for F9.6 (server idempotency, parked).
        await enqueue({ op: { name: 'addOrderItem', orderId: current.id, vars: { input: draftToAddItemInput(draft) } }, kind: `agregar ${draft.quantity}× ${draft.name}` })
        const offlineItem = makeOfflineItem(draft)
        const updated = bumpTotals({ ...current, items: [...current.items, offlineItem] }, offlineItem.totalCents)
        set((s) => ({ order: updated, pendingItemIds: new Set([...s.pendingItemIds, offlineItem.id]) }))
      }
    },

    async updateItemQty(itemId, quantity) {
      const current = get().order
      if (!current) return
      // Si el item es offline (todavía no subió), mutamos localmente.
      if (itemId.startsWith(OFFLINE_PREFIX)) {
        const items = current.items.map((i) => (i.id === itemId ? { ...i, quantity, totalCents: Math.round(i.totalCents / Math.max(1, i.quantity)) * quantity } : i))
        const delta = items.reduce((s, it) => s + it.totalCents, 0) - current.items.reduce((s, it) => s + it.totalCents, 0)
        const updated = bumpTotals({ ...current, items }, delta)
        set({ order: updated })
        return
      }
      try {
        const next = adaptOrder(await orders.updateOrderItem(current.id, itemId, { quantity }))
        set({ order: next, pendingItemIds: computePendingIds(next) })
      } catch (e) {
        if (!isTransportOffline(e)) throw e
        await enqueue({ op: { name: 'updateOrderItem', orderId: current.id, vars: { itemId, input: { quantity } } }, kind: `actualizar cantidad` })
      }
    },

    async removeItem(itemId, managerAuthToken) {
      const current = get().order
      if (!current) return
      if (itemId.startsWith(OFFLINE_PREFIX)) {
        const it = current.items.find((i) => i.id === itemId)
        const updated = bumpTotals({ ...current, items: current.items.filter((i) => i.id !== itemId) }, -(it?.totalCents ?? 0))
        set((s) => { const p = new Set(s.pendingItemIds); p.delete(itemId); return { order: updated, pendingItemIds: p } })
        return
      }
      try {
        const next = adaptOrder(await orders.removeOrderItem(current.id, itemId, managerAuthToken))
        set({ order: next, pendingItemIds: computePendingIds(next) })
      } catch (e) {
        if (!isTransportOffline(e)) throw e
        await enqueue({ op: { name: 'removeOrderItem', orderId: current.id, vars: { itemId } }, kind: 'quitar producto' })
        const it = current.items.find((i) => i.id === itemId)
        const updated = bumpTotals({ ...current, items: current.items.filter((i) => i.id !== itemId) }, -(it?.totalCents ?? 0))
        set((s) => { const p = new Set(s.pendingItemIds); p.delete(itemId); return { order: updated, pendingItemIds: p } })
      }
    },

    async sendToKitchen() {
      const current = get().order
      if (!current) return
      try {
        const next = adaptOrder(await orders.fireOrder(current.id))
        set({ order: next, pendingItemIds: computePendingIds(next) })
        // Auto-print de comanda de cocina al bridge (best-effort, no bloquea).
        // El "fire" pasó al backend; si autoPrint.kds.enabled mandamos también el
        // ticket impreso al bridge. Cualquier fallo va al history del store.
        try {
          const cfg = getPrintingConfig()
          if (cfg.autoPrint.kds.enabled) {
            const tenantId = useAuthStore.getState().activeTenantId()
            const header = getReceiptOpts(tenantId)
            const payload = toKitchen(next, { header, copies: cfg.autoPrint.kds.copies })
            printReceipt(payload, { printer: cfg.defaultPrinter, kind: 'kitchen' }).then(
              // relay: jobId → sigue progreso (tracker hace history real); host: history 'ok'.
              (r) => { if (r.jobId) trackRelayJob(r.jobId, 'Comanda', 'kitchen'); else pushPrintHistory('kitchen', 'ok') },
              (err: unknown) => pushPrintHistory('kitchen', 'error', err instanceof Error ? err.message : String(err)),
            )
          }
        } catch (bridgeErr) {
          pushPrintHistory('kitchen', 'error', bridgeErr instanceof Error ? bridgeErr.message : String(bridgeErr))
        }
      } catch (e) {
        if (!isTransportOffline(e)) throw e
        await enqueue({ op: { name: 'fireOrder', orderId: current.id, vars: {} }, kind: 'enviar a cocina' })
        const updated: OrderDto = { ...current, items: current.items.map((i) => (i.status === 'PENDING' ? { ...i, status: 'SENT' as const } : i)) }
        set({ order: updated, pendingItemIds: new Set() })
      }
    },

    async cancelOrder(reason, managerAuthToken) {
      const current = get().order
      if (!current) throw new Error('No hay orden abierta')
      const next = adaptOrder(await orders.cancelOrder(current.id, reason, managerAuthToken))
      set({ order: next, pendingItemIds: computePendingIds(next) })
      return next
    },

    resetOrder: () => {
      writeCurrentOrderId(null)
      set({ order: null, pendingItemIds: new Set(), payments: [], syncing: false })
    },

    hydrateOrder: (order) => {
      const prevId = get().order?.id
      const payments = prevId === order.id ? get().payments : []
      writeCurrentOrderId(order.id)
      set({ order, pendingItemIds: computePendingIds(order), payments })
    },

    async hydrateFromBackend(orderId) {
      set({ syncing: true })
      try {
        const fresh = adaptOrder(await orders.getOrder(orderId))
        // D4: a CLOSED/CANCELLED order is no longer the "active" order — clearing it
        // makes /order fall back to the empty "Selecciona/abre una mesa" state instead
        // of showing the finished sale with a live "Cobrar" button.
        if (fresh.status === 'CLOSED' || fresh.status === 'CANCELLED') {
          writeCurrentOrderId(null)
          set({ order: null, pendingItemIds: new Set(), payments: [], syncing: false })
          return null
        }
        const prevId = get().order?.id
        const payments = prevId === fresh.id ? get().payments : []
        writeCurrentOrderId(fresh.id)
        set({ order: fresh, pendingItemIds: computePendingIds(fresh), payments, syncing: false })
        return fresh
      } catch {
        writeCurrentOrderId(null)
        set({ order: null, pendingItemIds: new Set(), payments: [], syncing: false })
        return null
      }
    },

    invalidateAndRefetch() {
      if (invalidateTimer) clearTimeout(invalidateTimer)
      invalidateTimer = setTimeout(() => {
        invalidateTimer = null
        const id = get().order?.id
        if (!id) return
        void get().hydrateFromBackend(id)
      }, 150)
    },

    async registerPayment(draft) {
      const current = get().order
      if (!current) throw new Error('No hay orden abierta')
      const { tenantId, branchId, userId } = authCtx()
      const idem = draft.idempotencyKey ?? newIdempotencyKey()
      const backendMethod = toBackendPaymentMethod(draft.method)
      // F9.11b: cada tender (incl. pagos mixtos Efectivo+Tarjeta) se persiste por el
      // grafo recordOrderPayment -> PosPayment completo (method + cardLastFour + reference
      // + changeCents + subAccount), idempotente. Se eliminó la llamada REST a
      // dhk8-pos-payments-mixed (stub que devolvía 404) — D2 cerrada.
      const next = adaptOrder(await orders.recordOrderPayment(current.id, {
        amountCents: draft.amountCents,
        method: backendMethod,
        tipCents: draft.tipCents ?? 0,
        ...(draft.subAccountId ? { subAccountId: draft.subAccountId } : {}),
        ...(draft.changeCents !== undefined ? { changeCents: draft.changeCents } : {}),
        ...(draft.cardLastFour ? { cardLastFour: draft.cardLastFour } : {}),
        capturedById: userId,
        // F9.6: same idem key — a retried tap dedupes to one payment.
        idempotencyKey: idem,
      }))
      // La propina ya NO se registra aquí: lo registra pos-tips al consumir
      // `dhk8.pos.order.closed` (services/pos-tips/src/saga/order-tips.ts).
      // Por qué se movió: esto era `void ...` best-effort desde el navegador y
      // sólo disparaba cuando la propina venía en el draft del PAGO — el
      // selector la fija a nivel ORDEN, así que en la práctica nunca corría y
      // `report_tips` quedaba vacío. Dejarlo además duplicaría el registro,
      // porque recordOrderPayment suma la propina del pago a la de la orden.
      const created: RegisteredPayment = {
        id: idem,
        method: draft.method,
        amountCents: draft.amountCents,
        tipCents: draft.tipCents ?? 0,
        cardLastFour: draft.cardLastFour ?? null,
        referenceCode: idem,
        createdAt: new Date().toISOString(),
        ...(draft.subAccountId ? { subAccountId: draft.subAccountId } : {}),
      }
      // D4: once the order is fully paid (CLOSED), drop the persisted order id so a
      // remount of /order does NOT rehydrate the closed sale (the in-memory order is
      // kept here so the PaymentDialog "Venta cerrada" ticket overlay can still render;
      // OrderPage clears it from the store when that dialog closes).
      if (next.status === 'CLOSED') writeCurrentOrderId(null)
      set((s) => ({ order: next, pendingItemIds: computePendingIds(next), payments: [...s.payments, created] }))
      return created
    },

    async setTip(tipCents) {
      const current = get().order
      if (!current) throw new Error('No hay orden abierta')
      const next = adaptOrder(await orders.setOrderTip(current.id, tipCents))
      set({ order: next, pendingItemIds: computePendingIds(next) })
      return next
    },

    async closeOrder(tipCents) {
      // The order closes when fully paid (via /payments); this just persists the tip
      // (the order total includes it). PaymentDialog should call setTip() before the
      // payments now — closeOrder() stays as an alias for older call sites.
      return get().setTip(tipCents)
    },

    async createSubAccount(label) {
      const current = get().order
      if (!current) throw new Error('No hay orden abierta')
      const next = adaptOrder(await orders.createOrderSubAccount(current.id, label))
      set((s) => ({ order: next, pendingItemIds: computePendingIds(next), payments: s.payments }))
      return next
    },

    async applyDiscount(draft) {
      const current = get().order
      if (!current) throw new Error('No hay orden abierta')
      const { userId } = authCtx()
      // Base the percentage off the targeted sub-account's subtotal when scoped.
      const scopedSub = draft.target === 'SUB_ACCOUNT' && draft.targetId
        ? current.subAccounts.find((s) => s.id === draft.targetId)
        : undefined
      const baseCents = scopedSub ? scopedSub.subtotalCents : current.subtotalCents
      const discountCents = draft.kind === 'PERCENT'
        ? Math.round((baseCents * draft.value) / 100)
        : Math.round(draft.value * 100)
      // The synthetic "Principal" bucket isn't a real sub-account — a discount scoped
      // to it is really an order-level discount (Principal already shows it).
      const scopedSubId = scopedSub && scopedSub.id !== PRINCIPAL_SUB_ID ? scopedSub.id : null
      const next = adaptOrder(await orders.applyOrderDiscount(current.id, {
        discountCents, reason: draft.reason ?? '', appliedBy: userId,
        ...(scopedSubId ? { subAccountId: scopedSubId } : {}),
      }))
      set({ order: next, pendingItemIds: computePendingIds(next) })
      return next
    },

    async splitOrder(draft) {
      const current = get().order
      if (!current) throw new Error('No hay orden abierta')
      // dhk8-pos-orders has no dedicated /split route — splitting = sub-accounts.
      // EVEN / PERCENT: create N sub-accounts with the requested labels (the per-part
      //   amount is a display concern; the server keeps order-level totals).
      // ITEMS: create the sub-accounts then move each assigned line into its bucket.
      // TODO backend: a real `POST /orders/:id/split` that records the split mode +
      //   percentages atomically (today we approximate with sub-accounts + item moves).
      const n =
        draft.mode === 'ITEMS'
          ? Math.max(2, draft.labels?.length ?? 2)
          : Math.max(2, draft.parts ?? draft.labels?.length ?? draft.percents?.length ?? 2)
      const labels = Array.from({ length: n }, (_, i) => draft.labels?.[i] ?? `Cuenta ${String.fromCharCode(65 + i)}`)
      let last = current
      // (Re)create sub-accounts up to `n` — backend appends, so only create the missing
      // ones. Exclude the synthetic "Principal" bucket from the count/ids.
      const realSubs = (o: OrderDto) => o.subAccounts.filter((s) => s.id !== PRINCIPAL_SUB_ID)
      const createdIds: string[] = realSubs(current).map((s) => s.id)
      for (let i = createdIds.length; i < n; i++) {
        const label = labels[i] ?? `Cuenta ${i + 1}`
        last = adaptOrder(await orders.createOrderSubAccount(current.id, label))
        const fresh = realSubs(last).filter((s) => !createdIds.includes(s.id))
        const created = fresh.find((s) => s.label === label) ?? fresh[fresh.length - 1]
        if (created) createdIds.push(created.id)
      }
      // Lane #166 — sync de labels existentes. Si el user editó una sub-cuenta
      // existente (ej. renombrar 'Cuenta A' → 'Juan'), antes el cambio se
      // perdía. Ahora persistimos los renames antes de mover items.
      const existing = realSubs(current)
      for (let i = 0; i < Math.min(existing.length, labels.length); i++) {
        const sa = existing[i]
        const desired = labels[i]
        if (sa && desired && sa.label !== desired) {
          try {
            last = adaptOrder(await orders.updateOrderSubAccount(current.id, sa.id, desired))
          } catch {
            // best-effort: si el rename falla, no abortamos el split entero
          }
        }
      }
      if (draft.mode === 'ITEMS' && draft.itemAssignments) {
        for (const [itemId, idx] of Object.entries(draft.itemAssignments)) {
          const subId = createdIds[idx]
          if (!subId) continue
          try {
            last = adaptOrder(await orders.assignOrderItemSubAccount(current.id, itemId, subId))
          } catch {
            // best-effort: the move endpoint may not exist yet — sub-accounts still created
            // TODO backend: /orders/:id/items/:itemId/sub-account
          }
        }
      }
      set({ order: last, pendingItemIds: computePendingIds(last) })
      return last
    },

    async assignItemToSub(itemId, subAccountId) {
      const current = get().order
      if (!current) throw new Error('No hay orden abierta')
      // PRINCIPAL_SUB_ID / null → un-assign (back to the Principal bucket).
      const target = !subAccountId || subAccountId === PRINCIPAL_SUB_ID ? null : subAccountId
      const next = adaptOrder(await orders.assignOrderItemSubAccount(current.id, itemId, target))
      set({ order: next, pendingItemIds: computePendingIds(next) })
      return next
    },

    async flushOutbox() {
      const send = async (entry: OutboxEntry): Promise<void> => {
        // Preferred path: the entry carries a graph mutation (order writes are GraphQL
        // now, F8.1). Replay it through the SAME client fns the online path uses so it
        // actually syncs - the old REST replay hit dead /v1/orders/... routes. The
        // idempotencyKey stays on the entry for F9.6 (server idempotency, parked).
        if (entry.op) {
          // F9.6: forward the entry's idempotency key so a replayed money-path op
          // (recordOrderPayment / refundOrder) dedupes into a single payment/refund.
          await orders.replayGraphOp(entry.op, entry.idempotencyKey)
          return
        }
        // Legacy REST descriptor (payments etc. still on REST by design). Unchanged.
        if (entry.url && entry.method) {
          const url = /^https?:\/\//.test(entry.url) ? entry.url : `${apiOrigin()}${entry.url}`
          await apiFetch(url, {
            method: entry.method,
            ...(entry.body !== undefined ? { body: entry.body } : {}),
            headers: { 'Idempotency-Key': entry.idempotencyKey },
          })
          return
        }
        throw new Error(`OUTBOX_ENTRY_NO_TARGET: ${entry.kind}`)
      }
      const res = await flush(send)
      const id = get().order?.id
      if (id && res.sent > 0 && res.remaining === 0) await get().hydrateFromBackend(id)
      return res
    },
  }
})
